/**
 * Defines the authenticated contract for internal case management.
 */
import { z } from "zod";
import { CaseClassificationSchema } from "./CaseReasonContract.js";
import { PublicCaseTypeSchema, PublicContractedItemTypeSchema, PublicCurrencySchema, PublicDocumentTypeSchema, PublicResponseChannelSchema, } from "./PublicCaseContract.js";
/**
 * Stable case lifecycle values. Response progress lives in the response workflow, not here.
 */
export const InternalCaseStatusSchema = z.enum([
    "RECEIVED",
    "PENDING_ASSIGNMENT",
    "IN_INVESTIGATION",
    "CLOSED",
]);
/**
 * Stable priority values backed by the case-priority catalog.
 */
export const InternalCasePrioritySchema = z.enum(["LOW", "NORMAL", "HIGH", "URGENT"]);
/**
 * Supported assignment projections for the work queue.
 */
export const InternalCaseAssignmentFilterSchema = z.enum(["ALL", "UNASSIGNED", "MINE"]);
/**
 * Explicit ordering allowlist for the work queue.
 */
export const InternalCaseSortSchema = z.enum([
    "RECEIVED_DESC",
    "RECEIVED_ASC",
    "PRIORITY_DESC",
    "SLA_DUE_ASC",
    "UPDATED_DESC",
]);
export const InternalCaseSlaStateSchema = z.enum([
    "ON_TRACK",
    "ATTENTION",
    "AT_RISK",
    "OVERDUE",
    "COMPLETED",
]);
/**
 * Query contract for server-side pagination and filters.
 */
export const InternalCaseListQuerySchema = z
    .object({
    assignment: InternalCaseAssignmentFilterSchema.default("ALL"),
    branchSlug: z.string().trim().min(1).max(120).optional(),
    from: z.iso.date().optional(),
    operationalType: PublicCaseTypeSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
    priority: InternalCasePrioritySchema.optional(),
    search: z.string().trim().min(1).max(200).optional(),
    slaState: InternalCaseSlaStateSchema.optional(),
    /**
     * Classification filters. The narrowest one supplied wins, so passing a
     * reason together with its category is redundant but never contradictory.
     */
    reasonCategoryId: z.uuid().optional(),
    reasonId: z.uuid().optional(),
    sort: InternalCaseSortSchema.default("RECEIVED_DESC"),
    status: InternalCaseStatusSchema.optional(),
    subReasonId: z.uuid().optional(),
    tagId: z.uuid().optional(),
    to: z.iso.date().optional(),
    /**
     * Restricts the queue to cases nobody has classified yet, which is how an
     * administrator finds the backlog after loading a catalog.
     */
    unclassified: z
        .enum(["true", "false"])
        .transform((value) => value === "true")
        .optional(),
})
    .strict()
    .refine((value) => value.from === undefined || value.to === undefined || value.from <= value.to, "The start date must not be after the end date.");
/**
 * Uses the stable public sequence as an internal URL identifier without treating it as authorization.
 */
export const InternalCaseParametersSchema = z
    .object({
    publicId: z.string().regex(/^\d+$/),
})
    .strict();
/**
 * Optional replay-protection header accepted by authenticated mutations.
 */
export const InternalCaseMutationHeadersSchema = z
    .object({
    "idempotency-key": z
        .string()
        .trim()
        .min(16)
        .max(128)
        .regex(/^[A-Za-z0-9._~-]+$/)
        .optional(),
})
    .strict();
const statusReferenceSchema = z
    .object({
    title: z.string(),
    value: InternalCaseStatusSchema,
})
    .strict();
const priorityReferenceSchema = z
    .object({
    title: z.string(),
    value: InternalCasePrioritySchema,
})
    .strict();
const caseTypeReferenceSchema = z
    .object({
    title: z.string(),
    value: PublicCaseTypeSchema,
})
    .strict();
const branchReferenceSchema = z
    .object({
    name: z.string(),
    slug: z.string(),
})
    .strict();
const assigneeReferenceSchema = z
    .object({
    fullName: z.string(),
    membershipId: z.uuid(),
})
    .strict();
const slaReferenceSchema = z
    .object({
    dueDate: z.iso.date(),
    remainingBusinessDays: z.number().int(),
    state: z
        .object({
        title: z.string(),
        value: InternalCaseSlaStateSchema,
    })
        .strict(),
})
    .strict();
/**
 * Minimal authorized case projection used by the queue.
 */
export const InternalCaseListItemSchema = z
    .object({
    assignedTo: assigneeReferenceSchema.nullable(),
    bookNumber: z.string().nullable(),
    branch: branchReferenceSchema.nullable(),
    classification: CaseClassificationSchema,
    consumerName: z.string(),
    isReopened: z.boolean(),
    operationalType: caseTypeReferenceSchema,
    priority: priorityReferenceSchema,
    publicId: z.string().regex(/^\d+$/),
    receivedAt: z.iso.datetime({ offset: true }),
    reopenCount: z.number().int().nonnegative(),
    sla: slaReferenceSchema,
    status: statusReferenceSchema,
    updatedAt: z.iso.datetime({ offset: true }),
    version: z.number().int().nonnegative(),
})
    .strict();
/**
 * Paginated work-queue response.
 */
export const InternalCaseListResponseSchema = z
    .object({
    items: z.array(InternalCaseListItemSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
})
    .strict();
const historyActorSchema = z
    .object({
    displayName: z.string(),
})
    .strict();
const statusHistorySchema = z
    .object({
    actor: historyActorSchema,
    changedAt: z.iso.datetime({ offset: true }),
    newStatus: statusReferenceSchema,
    previousStatus: statusReferenceSchema.nullable(),
    reason: z.string(),
})
    .strict();
const assignmentHistorySchema = z
    .object({
    actor: historyActorSchema,
    changedAt: z.iso.datetime({ offset: true }),
    newAssignee: assigneeReferenceSchema.nullable(),
    previousAssignee: assigneeReferenceSchema.nullable(),
    reason: z.string(),
})
    .strict();
const priorityHistorySchema = z
    .object({
    actor: historyActorSchema,
    changedAt: z.iso.datetime({ offset: true }),
    newPriority: priorityReferenceSchema,
    previousPriority: priorityReferenceSchema.nullable(),
    reason: z.string(),
})
    .strict();
const branchHistorySchema = z
    .object({
    actor: historyActorSchema,
    changedAt: z.iso.datetime({ offset: true }),
    newBranch: branchReferenceSchema.nullable(),
    previousBranch: branchReferenceSchema.nullable(),
    reason: z.string(),
})
    .strict();
const classificationHistorySchema = z
    .object({
    actor: historyActorSchema,
    changedAt: z.iso.datetime({ offset: true }),
    newType: caseTypeReferenceSchema,
    previousType: caseTypeReferenceSchema,
    reason: z.string(),
})
    .strict();
/**
 * Movement of the configurable classification, distinct from the reclamo/queja
 * reclassification recorded in `classifications`.
 */
const reasonHistorySchema = z
    .object({
    actor: historyActorSchema,
    changedAt: z.iso.datetime({ offset: true }),
    newClassification: CaseClassificationSchema,
    previousClassification: CaseClassificationSchema,
    reason: z.string(),
})
    .strict();
const closureHistorySchema = z
    .object({
    actor: historyActorSchema,
    closedAt: z.iso.datetime({ offset: true }),
    reason: z.string(),
})
    .strict();
const reopenHistorySchema = z
    .object({
    actor: historyActorSchema,
    cycleNumber: z.number().int().positive(),
    reason: z.string(),
    reopenedAt: z.iso.datetime({ offset: true }),
    reopenedTo: statusReferenceSchema,
})
    .strict();
/**
 * Compact tag reference projected on an authorized case.
 */
export const InternalCaseTagReferenceSchema = z
    .object({
    code: z.string().min(1).max(40),
    color: z.string().regex(/^#[0-9A-F]{6}$/u),
    id: z.uuid(),
    name: z.string().min(1).max(120),
})
    .strict();
const tagHistorySchema = z
    .object({
    actor: historyActorSchema,
    changeType: z.enum(["ADDED", "REMOVED"]),
    changedAt: z.iso.datetime({ offset: true }),
    reason: z.string(),
    tag: InternalCaseTagReferenceSchema,
})
    .strict();
/**
 * Sticky-note presentation palette. Stored and returned as uppercase #RRGGBB.
 */
export const InternalCaseNoteColorPresetSchema = z.enum([
    "YELLOW",
    "AMBER",
    "LIME",
    "MINT",
    "SKY",
    "LAVENDER",
    "ROSE",
    "PEACH",
]);
export const InternalCaseNoteColorPresetHex = Object.freeze({
    AMBER: "#FFE082",
    LAVENDER: "#E1BEE7",
    LIME: "#C5E1A5",
    MINT: "#B2DFDB",
    PEACH: "#FFCCBC",
    ROSE: "#F8BBD0",
    SKY: "#B3E5FC",
    YELLOW: "#FFF59D",
});
/**
 * Accepts a sticky preset token or #RRGGBB and normalizes to uppercase hex.
 */
export const InternalCaseNoteColorSchema = z
    .string()
    .trim()
    .transform((value, context) => {
    const normalized = value.toUpperCase();
    const preset = InternalCaseNoteColorPresetSchema.safeParse(normalized);
    if (preset.success) {
        return InternalCaseNoteColorPresetHex[preset.data];
    }
    const hex = normalized.startsWith("#") ? normalized : `#${normalized}`;
    if (!/^#[0-9A-F]{6}$/u.test(hex)) {
        context.addIssue({
            code: "custom",
            message: "Expected a sticky preset color or #RRGGBB value.",
        });
        return z.NEVER;
    }
    return hex;
});
/**
 * Append-only internal note projection. Superseded content remains traceable.
 */
export const InternalCaseNoteSchema = z
    .object({
    author: historyActorSchema,
    color: InternalCaseNoteColorSchema,
    content: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    id: z.uuid(),
    reason: z.string().nullable(),
    supersedesNoteId: z.uuid().nullable(),
})
    .strict();
const internalConsumerSchema = z
    .object({
    address: z.string(),
    documentNumber: z.string(),
    documentType: PublicDocumentTypeSchema,
    email: z.string().nullable(),
    fullName: z.string(),
    isMinor: z.boolean(),
    phone: z.string(),
    requestedResponseChannel: PublicResponseChannelSchema,
})
    .strict();
const caseManagementPermissionsSchema = z
    .object({
    canAddNote: z.boolean(),
    canAssign: z.boolean(),
    canChangeBranch: z.boolean(),
    canChangePriority: z.boolean(),
    canClose: z.boolean(),
    canManageTags: z.boolean(),
    canReclassify: z.boolean(),
    canRevealAccessToken: z.boolean(),
    canReopen: z.boolean(),
    canTransition: z.boolean(),
})
    .strict();
/**
 * Administrator-only reveal of the consumer case access credential.
 */
export const RevealCaseAccessTokenResponseSchema = z
    .object({
    accessToken: z.string().regex(/^lrc_[A-Za-z0-9_-]{16}\.[A-Za-z0-9_-]{43}$/),
    expiresAt: z.iso.datetime({ offset: true }),
    revoked: z.boolean(),
})
    .strict();
export const CaseCloseWarningSchema = z.enum([
    "MISSING_BRANCH",
    "MISSING_ASSIGNEE",
    "MISSING_EVIDENCE",
    "MISSING_FINAL_RESPONSE",
    "MISSING_COMMUNICATION",
    "OPEN_FOLLOW_UPS",
]);
export const CaseCloseReadinessSchema = z
    .object({
    warnings: z.array(CaseCloseWarningSchema),
})
    .strict();
/**
 * Authorized internal case detail. It never forms part of the consumer-facing contract.
 */
export const InternalCaseDetailResponseSchema = z
    .object({
    assignedTo: assigneeReferenceSchema.nullable(),
    assignmentCandidates: z.array(assigneeReferenceSchema),
    availableBranches: z.array(branchReferenceSchema),
    availableTransitions: z.array(statusReferenceSchema),
    bookNumber: z.string().nullable(),
    branch: branchReferenceSchema.nullable(),
    claim: z
        .object({
        detail: z.string(),
        operationalType: caseTypeReferenceSchema,
        originalType: caseTypeReferenceSchema,
        requestedAction: z.string(),
    })
        .strict(),
    classification: CaseClassificationSchema,
    closeReadiness: CaseCloseReadinessSchema,
    consumer: internalConsumerSchema,
    contractedItem: z
        .object({
        claimedAmount: z
            .object({
            currency: PublicCurrencySchema,
            value: z.string(),
        })
            .strict()
            .nullable(),
        description: z.string(),
        type: PublicContractedItemTypeSchema,
    })
        .strict(),
    history: z
        .object({
        assignments: z.array(assignmentHistorySchema),
        branches: z.array(branchHistorySchema),
        classifications: z.array(classificationHistorySchema),
        closures: z.array(closureHistorySchema),
        priorities: z.array(priorityHistorySchema),
        reasons: z.array(reasonHistorySchema),
        reopens: z.array(reopenHistorySchema),
        statuses: z.array(statusHistorySchema),
        tags: z.array(tagHistorySchema),
    })
        .strict(),
    incidentDate: z.iso.date(),
    isReopened: z.boolean(),
    notes: z.array(InternalCaseNoteSchema),
    permissions: caseManagementPermissionsSchema,
    priority: priorityReferenceSchema,
    publicId: z.string().regex(/^\d+$/),
    receivedAt: z.iso.datetime({ offset: true }),
    reopenCount: z.number().int().nonnegative(),
    sla: slaReferenceSchema,
    status: statusReferenceSchema,
    tags: z.array(InternalCaseTagReferenceSchema),
    version: z.number().int().nonnegative(),
})
    .strict();
const expectedVersionSchema = z.number().int().nonnegative();
const reasonSchema = z.string().trim().min(5).max(1_000);
export const AssignInternalCaseRequestSchema = z
    .object({
    expectedVersion: expectedVersionSchema,
    membershipId: z.uuid().nullable(),
    reason: reasonSchema,
})
    .strict();
export const ChangeInternalCasePriorityRequestSchema = z
    .object({
    expectedVersion: expectedVersionSchema,
    priority: InternalCasePrioritySchema,
    reason: reasonSchema,
})
    .strict();
export const ChangeInternalCaseBranchRequestSchema = z
    .object({
    branchSlug: z.string().trim().min(1).max(120).nullable(),
    expectedVersion: expectedVersionSchema,
    reason: reasonSchema,
})
    .strict();
export const TransitionInternalCaseRequestSchema = z
    .object({
    expectedVersion: expectedVersionSchema,
    reason: reasonSchema,
    status: InternalCaseStatusSchema,
})
    .strict();
export const ReopenInternalCaseRequestSchema = z
    .object({
    expectedVersion: expectedVersionSchema,
    reason: reasonSchema,
    reopenTo: z.enum(["IN_INVESTIGATION"]),
})
    .strict();
export const ReclassifyInternalCaseRequestSchema = z
    .object({
    expectedVersion: expectedVersionSchema,
    operationalType: PublicCaseTypeSchema,
    reason: reasonSchema,
})
    .strict();
/**
 * Sets or clears the configurable classification. All three levels travel
 * together so the hierarchy is validated as a unit; clearing means null at
 * every level.
 */
export const SetInternalCaseReasonRequestSchema = z
    .object({
    expectedVersion: expectedVersionSchema,
    reason: reasonSchema,
    reasonCategoryId: z.uuid().nullable(),
    reasonId: z.uuid().nullable(),
    subReasonId: z.uuid().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.reasonId !== null && value.reasonCategoryId === null) {
        context.addIssue({
            code: "custom",
            message: "A reason requires its category.",
            path: ["reasonCategoryId"],
        });
    }
    if (value.subReasonId !== null && value.reasonId === null) {
        context.addIssue({
            code: "custom",
            message: "A sub-reason requires its reason.",
            path: ["reasonId"],
        });
    }
});
export const AddInternalCaseNoteRequestSchema = z
    .object({
    color: InternalCaseNoteColorSchema.optional(),
    content: z.string().trim().min(1).max(4_000),
    expectedVersion: expectedVersionSchema,
})
    .strict();
export const CorrectInternalCaseNoteRequestSchema = z
    .object({
    color: InternalCaseNoteColorSchema.optional(),
    content: z.string().trim().min(1).max(4_000),
    expectedVersion: expectedVersionSchema,
    noteId: z.uuid(),
    reason: reasonSchema,
})
    .strict();
export const ChangeInternalCaseNoteColorRequestSchema = z
    .object({
    color: InternalCaseNoteColorSchema,
    expectedVersion: expectedVersionSchema,
    noteId: z.uuid(),
})
    .strict();
export const AddInternalCaseTagRequestSchema = z
    .object({
    expectedVersion: expectedVersionSchema,
    reason: reasonSchema,
    tagId: z.uuid(),
})
    .strict();
export const RemoveInternalCaseTagRequestSchema = z
    .object({
    expectedVersion: expectedVersionSchema,
    reason: reasonSchema,
    tagId: z.uuid(),
})
    .strict();
/**
 * Uniform response for committed or idempotently replayed internal mutations.
 */
export const InternalCaseMutationResponseSchema = z
    .object({
    publicId: z.string().regex(/^\d+$/),
    version: z.number().int().nonnegative(),
    wasReplay: z.boolean(),
})
    .strict();
/**
 * Versioned outbox event name for committed internal changes.
 */
export const CaseManagementChangedEventName = "cases.management-changed.v1";
/**
 * Non-PII payload written after any internal case mutation.
 */
export const CaseManagementChangedEventPayloadSchema = z
    .object({
    branchId: z.uuid().nullable(),
    caseId: z.uuid(),
    changeType: z.enum([
        "ASSIGNMENT",
        "BRANCH",
        "CLASSIFICATION",
        "NOTE",
        "PRIORITY",
        "REASON",
        "REOPEN",
        "STATUS",
        "TAG",
    ]),
    companyId: z.uuid(),
    occurredAt: z.iso.datetime({ offset: true }),
    publicId: z.string().regex(/^\d+$/),
    version: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=InternalCaseContract.js.map